import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone, Send, Instagram, Linkedin, Youtube, Twitter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";
import { EMAIL, ADDRESS, WHATSAPP_LINK } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll get back within 1 business day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Section className="pt-24 pb-10">
        <FadeIn><Eyebrow>Contact us</Eyebrow></FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">Let&apos;s get your registers off paper.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6">Tell us about your team and the registers you&apos;d like to digitize. We typically respond within one business day.</Lead>
        </FadeIn>
      </Section>

      <Section className="pt-0">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-3xl border border-zinc-200 bg-white p-6 md:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="text-xs font-medium text-zinc-600">Full name</Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="mt-1.5 h-11 rounded-lg border-zinc-200"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-medium text-zinc-600">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-1.5 h-11 rounded-lg border-zinc-200"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="phone" className="text-xs font-medium text-zinc-600">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 90585 20747"
                    className="mt-1.5 h-11 rounded-lg border-zinc-200"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-xs font-medium text-zinc-600">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Which registers would you like to digitize?"
                    className="mt-1.5 rounded-lg border-zinc-200"
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="mt-6 rounded-full bg-zinc-950 px-6 text-white hover:bg-zinc-800"
              >
                {loading ? "Sending…" : (<>Send message <Send className="ml-1.5 h-4 w-4" /></>)}
              </Button>
              <p className="mt-3 text-xs text-zinc-500">By submitting, you agree to our Privacy Policy.</p>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-6 md:p-8">
              <div className="space-y-5">
                <InfoRow icon={Mail} label="Email" value={<a href={`mailto:${EMAIL}`} className="hover:text-zinc-950" data-testid="contact-info-email">{EMAIL}</a>} />
                <InfoRow icon={MapPin} label="Address" value={ADDRESS} />
                <InfoRow icon={Phone} label="WhatsApp" value={<a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="hover:text-zinc-950" data-testid="contact-info-whatsapp">+91 90585 20747</a>} />
              </div>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-cta"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 py-3 text-sm font-medium text-white hover:bg-zinc-800"
              >
                <MessageCircle className="h-4 w-4" /> Chat with us on WhatsApp
              </a>

              <div className="mt-6 flex items-center gap-3">
                {[
                  { Icon: Instagram, href: "https://instagram.com", label: "instagram" },
                  { Icon: Linkedin, href: "https://linkedin.com", label: "linkedin" },
                  { Icon: Youtube, href: "https://youtube.com", label: "youtube" },
                  { Icon: Twitter, href: "https://twitter.com", label: "twitter" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-5 overflow-hidden rounded-3xl border border-zinc-200">
              <iframe
                title="Meerut location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Meerut,Uttar%20Pradesh,India&output=embed"
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white border border-zinc-200">
        <Icon className="h-4 w-4 text-zinc-900" />
      </div>
      <div>
        <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-800">{value}</div>
      </div>
    </div>
  );
}
